import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, Typography, IconButton, Modal } from '@mui/material';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '1/1',
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
  maxWidth: '600px',
  margin: '0 auto',
}));

const CarouselImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '24px',
  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'scale(1.02)',
  }
});

const CarouselButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: '#FF6B35',
  padding: '12px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'translateY(-50%) scale(1.1)',
  },
  transition: 'all 0.5s ease',
  zIndex: 2,
}));

const CAROUSEL_IMAGES = [
  "https://dp9h3awfnhccl.cloudfront.net/public_folder/carousel-1.jpg",
  "https://dp9h3awfnhccl.cloudfront.net/public_folder/carousel-3.jpg",
  "https://dp9h3awfnhccl.cloudfront.net/public_folder/carousel-4.jpg",
  "https://dp9h3awfnhccl.cloudfront.net/public_folder/carousel-2.jpg",
];

const VIDEO_URLS = [
  process.env.REACT_APP_VIDEO_CAROUSEL1 || "",
  process.env.REACT_APP_VIDEO_CAROUSEL2 || "",
  process.env.REACT_APP_VIDEO_CAROUSEL3 || "",
  process.env.REACT_APP_VIDEO_CAROUSEL4 || ""
];

// Configuration: Set to true to show video icon, false to hide it
const SHOW_VIDEO_ICON = [
  true,  // carousel-1.jpg - has video
  true,  // carousel-3.jpg - has video
  true,  // carousel-4.jpg - has video
  false  // carousel-2.jpg - no video
];

const VideoModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(10px)',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
}));

const VideoModalContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '90vw',
  maxWidth: '500px',
  background: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '20px',
  backdropFilter: 'blur(15px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
  padding: theme.spacing(2),
  outline: 'none',
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '400px',
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  color: 'white',
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgba(255, 107, 53, 0.8)',
    transform: 'scale(1.1)',
  },
  transition: 'all 0.3s ease'
}));

const VideoIconOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 3,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translate(-50%, -50%) scale(1.2)',
  }
}));

const Testimonials = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');

  const handleCarouselNav = useCallback((direction) => {
    if (direction === 'next') {
      setCurrentImage(prev => (prev + 1) % CAROUSEL_IMAGES.length);
    } else {
      setCurrentImage(prev => (prev - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleVideoClick = (index) => {
    setCurrentVideoUrl(VIDEO_URLS[index]);
    setVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
    setCurrentVideoUrl('');
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 } }}>
      <Typography
        variant="h3"
        align="center"
        sx={{
          mb: 6,
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 800,
          '& span': {
            background: 'linear-gradient(45deg, #06AED5 30%, #4FC3F7 70%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }
        }}
      >
        <span>Trusted by Parents</span> Loved by Kids!
      </Typography>
      
      <Box sx={{ px: { xs: 2, sm: 4, md: 6 }, position: 'relative' }}>
        <CarouselContainer>
          <Box sx={{ 
            position: 'absolute', 
            top: 0,
            left: 0,
            width: '100%', 
            height: '100%',
            display: 'flex',
            overflow: 'hidden'
          }}>
            {CAROUSEL_IMAGES.map((image, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  left: 0,
                  width: '100%',
                  height: '100%',
                  transform: `translateX(${(index - currentImage) * 100}%)`,
                  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CarouselImage
                  src={image}
                  alt={`Testimonial ${index + 1}`}
                  sx={{ padding: 0 }}
                />
                {index === currentImage && SHOW_VIDEO_ICON[index] && (
                  <VideoIconOverlay onClick={() => handleVideoClick(index)}>
                    <PlayCircleOutlineIcon
                      sx={{
                        fontSize: { xs: '4rem', sm: '5rem', md: '6rem' },
                        color: 'white',
                        filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.6))',
                        opacity: 0.9,
                      }}
                    />
                  </VideoIconOverlay>
                )}
              </Box>
            ))}
          </Box>

          <CarouselButton
            onClick={() => handleCarouselNav('prev')}
            sx={{ left: 8, zIndex: 10 }}
            aria-label="Previous testimonial"
          >
            <ChevronLeftIcon fontSize="small" />
          </CarouselButton>
          
          <CarouselButton
            onClick={() => handleCarouselNav('next')}
            sx={{ right: 8 }}
            aria-label="Next testimonial"
          >
            <ChevronRightIcon fontSize="small" />
          </CarouselButton>
        </CarouselContainer>
        
        {/* Carousel indicators */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 2,
          gap: 2
        }}>
          {CAROUSEL_IMAGES.map((_, index) => (
            <Box 
              key={index}
              onClick={() => setCurrentImage(index)}
              sx={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: index === currentImage ? '#FF6B35' : 'rgba(255, 255, 255, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.4s ease',
                transform: index === currentImage ? 'scale(1.3)' : 'scale(1)',
                '&:hover': {
                  backgroundColor: index === currentImage ? '#FF6B35' : 'rgba(255, 255, 255, 0.5)',
                }
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Video Modal */}
      <VideoModal
        open={videoModalOpen}
        onClose={handleCloseVideoModal}
        closeAfterTransition
      >
        <VideoModalContent>
          <CloseButton onClick={handleCloseVideoModal}>
            <CloseIcon />
          </CloseButton>
          <Box sx={{ position: 'relative', paddingTop: '177.78%', borderRadius: '16px', overflow: 'hidden' }}>
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '16px',
              }}
              src={currentVideoUrl}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </Box>
        </VideoModalContent>
      </VideoModal>
    </Container>
  );
};

export default Testimonials;
